import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Avatar
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  Link as LinkIcon,
  QrCode as QrCodeIcon,
  Image as ImageIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import trackingService, { AnalyticsSummary, TrackableItemType, SocialPlatform } from '../../../../services/trackingService';
import visualAssetService, { SavedAsset } from '../../../../services/visualAssetService';

interface EventAnalyticsTabProps {
  organizationId: string;
  eventId: string;
  eventTitle?: string;
}

const EventAnalyticsTab: React.FC<EventAnalyticsTabProps> = ({
  organizationId,
  eventId,
  eventTitle
}) => {
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [assets, setAssets] = useState<SavedAsset[]>([]);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch event analytics
        const eventAnalytics = await trackingService.getEventAnalytics(eventId);
        setAnalytics(eventAnalytics);
        
        // Fetch assets list to display thumbnails and descriptions
        const assetsList = await visualAssetService.fetchEventAssets(organizationId, eventId);
        setAssets(assetsList);
      } catch (err) {
        console.error('Error loading analytics:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadAnalytics();
  }, [organizationId, eventId]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const getItemTypeIcon = (itemType: TrackableItemType) => {
    switch (itemType) {
      case 'event':
        return <EventIcon fontSize="small" sx={{ mr: 1 }} />;
      case 'asset':
        return <ImageIcon fontSize="small" sx={{ mr: 1 }} />;
      case 'qrcode':
        return <QrCodeIcon fontSize="small" sx={{ mr: 1 }} />;
      case 'post':
        return <Typography variant="body2" sx={{ mr: 1 }}>📝</Typography>;
      default:
        return <LinkIcon fontSize="small" sx={{ mr: 1 }} />;
    }
  };
  
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <FacebookIcon fontSize="small" style={{ color: '#1877F2' }} />;
      case 'twitter':
        return <TwitterIcon fontSize="small" style={{ color: '#1DA1F2' }} />;
      case 'linkedin':
        return <LinkedInIcon fontSize="small" style={{ color: '#0A66C2' }} />;
      case 'instagram':
        return <InstagramIcon fontSize="small" style={{ color: '#E4405F' }} />;
      case 'email':
        return <EmailIcon fontSize="small" style={{ color: '#EA4335' }} />;
      case 'whatsapp':
        return <WhatsAppIcon fontSize="small" style={{ color: '#25D366' }} />;
      case 'direct':
        return <LinkIcon fontSize="small" style={{ color: '#5E35B1' }} />;
      default:
        return <LinkIcon fontSize="small" />;
    }
  };
  
  // Find asset details by ID
  const getAssetDetails = (assetId: string) => {
    return assets.find(asset => asset.id === assetId);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (!analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No analytics data is available for this event yet. Try sharing assets on social media to start tracking performance.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="Overview" />
          <Tab label="By Channel" />
          <Tab label="By Asset" />
          <Tab label="Timeline" />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <Box sx={{ p: 1 }}>
          <Typography variant="h5" gutterBottom>
            {eventTitle ? `${eventTitle} - Analytics` : 'Event Analytics'}
          </Typography>
          
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
                    Total Shares
                  </Typography>
                  <Typography variant="h3" align="center" color="primary">
                    {analytics.totalShares}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
                    Total Clicks
                  </Typography>
                  <Typography variant="h3" align="center" color="primary">
                    {analytics.totalClicks}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
                    Click-Through Rate
                  </Typography>
                  <Typography variant="h3" align="center" color="primary">
                    {(analytics.clickThroughRate * 100).toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Top Performing Assets */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Top Performing Items
          </Typography>
          
          {analytics.topPerformingItems.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Shares</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.topPerformingItems.map((item) => {
                    const asset = item.itemType === 'asset' ? getAssetDetails(item.itemId) : null;
                    
                    return (
                      <TableRow key={`${item.itemType}-${item.itemId}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.itemType === 'asset' && asset ? (
                              <Avatar src={asset.imageUrl} alt={asset.title} sx={{ width: 32, height: 32, mr: 1 }} />
                            ) : (
                              getItemTypeIcon(item.itemType)
                            )}
                            <Box>
                              <Typography variant="body2" component="div">
                                {item.title || (asset ? asset.title : `${item.itemType} ${item.itemId.substring(0, 6)}...`)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.itemType}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.shares}</TableCell>
                        <TableCell align="right">{item.clicks}</TableCell>
                        <TableCell align="right">{(item.ctr * 100).toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No performance data available yet. Try sharing your event assets!
            </Alert>
          )}
        </Box>
      )}
      
      {/* By Channel Tab */}
      {tabValue === 1 && (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom>
            Performance by Channel
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Channel</TableCell>
                  <TableCell align="right">Shares</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">CTR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(analytics.sharesByPlatform)
                  .filter(([_, shares]) => shares > 0)
                  .sort(([_, sharesA], [__, sharesB]) => sharesB - sharesA)
                  .map(([platform, shares]) => {
                    const clicks = analytics.clicksByPlatform[platform as SocialPlatform] || 0;
                    const ctr = shares > 0 ? (clicks / shares) * 100 : 0;
                    
                    return (
                      <TableRow key={platform}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getPlatformIcon(platform)}
                            <Typography sx={{ ml: 1 }}>
                              {platform.charAt(0).toUpperCase() + platform.slice(1)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{shares}</TableCell>
                        <TableCell align="right">{clicks}</TableCell>
                        <TableCell align="right">{ctr.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {Object.values(analytics.sharesByPlatform).every(shares => shares === 0) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No channel data available yet. Try sharing your event through different social platforms!
            </Alert>
          )}
        </Box>
      )}
      
      {/* By Asset Tab */}
      {tabValue === 2 && (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom>
            Performance by Asset
          </Typography>
          
          {assets.length > 0 ? (
            <Grid container spacing={3}>
              {assets.map(asset => {
                // Find this asset in top performing items
                const assetStats = analytics.topPerformingItems.find(
                  item => item.itemType === 'asset' && item.itemId === asset.id
                );
                
                const shares = assetStats?.shares || 0;
                const clicks = assetStats?.clicks || 0;
                const ctr = shares > 0 ? (clicks / shares) * 100 : 0;
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={asset.id}>
                    <Card variant="outlined">
                      <Box sx={{ position: 'relative' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', mb: 2 }}>
                            <Avatar 
                              src={asset.imageUrl} 
                              alt={asset.title}
                              variant="rounded"
                              sx={{ width: 60, height: 60, mr: 2 }}
                            />
                            <Box>
                              <Typography variant="subtitle1" component="div">
                                {asset.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {asset.type}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Shares
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {shares}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Clicks
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {clicks}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                CTR
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {ctr.toFixed(1)}%
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Alert severity="info">
              No assets found for this event. Generate some assets first!
            </Alert>
          )}
        </Box>
      )}
      
      {/* Timeline Tab */}
      {tabValue === 3 && (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom>
            Performance Timeline
          </Typography>
          
          {Object.keys(analytics.sharesByDate).length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Shares</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(analytics.sharesByDate)
                    .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
                    .map(([date, shares]) => {
                      const clicks = analytics.clicksByDate[date] || 0;
                      const ctr = shares > 0 ? (clicks / shares) * 100 : 0;
                      
                      return (
                        <TableRow key={date}>
                          <TableCell>{format(new Date(date), 'MMM d, yyyy')}</TableCell>
                          <TableCell align="right">{shares}</TableCell>
                          <TableCell align="right">{clicks}</TableCell>
                          <TableCell align="right">{ctr.toFixed(1)}%</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No timeline data available yet.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EventAnalyticsTab;
